import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import withLoading from 'hocs/withLoading';
import _ from 'lodash';

import useTableCellPendingChangeAuthors from 'hooks/useTableCellPendingChangeAuthors';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { deleteLoanLiabilityAction } from 'store/loanApplications/actions';
import {
  selectFinancialInfoPropertyAddressById,
  selectIsClosedHidden,
  selectSortedBorrowerLiabilities,
} from 'store/loanApplications/selectors';

import { filterClosedLiabilities } from '../../FinancialInfo.utils';
import AdditionalNavigationContent from '../../UpdateTableModal/Content/AdditionalComponents/AdditionalNavigationContent';
import { normalizeLiabilitiesPendingChanges } from '../../UpdateTableModal/Content/SectionContent/Liabilities/Liabilities.utils';
import {
  FINANCIAL_INFO_TABLE_NAME,
  getDataWithNotFoundMessage,
  TablePropTypes,
} from '../FinancialInfoBody.constants';
import { tableStyles } from '../FinancialInfoBody.styles';
import { getTotalPendingChangesCount } from '../FinancialInfoBody.utils';
import useNeededCountByRecord from '../hooks/useNeededCountByRecord';
import TableWrapper from '../TableWrapper';

import { getConfig } from './Liabilities.config';
import { COLLAPSED_ROW_COUNT, EXPANDED_ROW_COUNT } from './Liabilities.constants';

const Liabilities = ({
  isLoading,
  onToggle,
  tableName,
  borrowerId,
  setIsLoading,
  onAddRecord,
  pendingChanges,
  chatTargets = null,
}) => {
  const dispatch = useDispatch();
  const { applicationId, subtab } = useParams();

  const isClosedHidden = useSelector(selectIsClosedHidden);
  const liabilities = useSelector(selectSortedBorrowerLiabilities(borrowerId));
  const propertyAddressById = useSelector(selectFinancialInfoPropertyAddressById);

  const isExpanded = subtab === tableName;

  const onAddClick = useCallback(() => onAddRecord(tableName), [onAddRecord, tableName]);

  const onRemove = useCallback(
    recordId => {
      setIsLoading(true);

      dispatch(
        deleteLoanLiabilityAction({
          liabilityId: recordId,
          tabId: LoanApplicationTabId.INCOME_AND_EMPLOYMENT,
          applicationId,
        }),
      )
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, setIsLoading],
  );

  const neededCountById = useNeededCountByRecord(tableName, liabilities);
  const getCellPendingChangeAuthors = useTableCellPendingChangeAuthors();

  const config = useMemo(
    () => getConfig({ isExpanded, onRemove, chatTargets, neededCountById, propertyAddressById }),
    [isExpanded, onRemove, chatTargets, neededCountById, propertyAddressById],
  );

  const formattedData = useMemo(() => {
    const filteredLiabilities = isClosedHidden ? filterClosedLiabilities(liabilities) : liabilities;

    return filteredLiabilities.length
      ? filteredLiabilities
      : getDataWithNotFoundMessage(config)[tableName](
          isExpanded ? EXPANDED_ROW_COUNT : COLLAPSED_ROW_COUNT,
        );
  }, [config, isExpanded, liabilities, tableName, isClosedHidden]);

  const liabilityIds = useMemo(() => liabilities.map(({ id }) => id), [liabilities]);

  const customFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.LIABILITY,
    entityIds: liabilityIds,
  });

  const pendingChangesCounts = calculatePendingChanges(
    _.merge(
      {},
      normalizeLiabilitiesPendingChanges({
        initialPendingChanges: pendingChanges,
        liabilities,
      }),
      customFieldsPendingChanges,
    ),
    true,
  );

  const totalPendingChangesCount = getTotalPendingChangesCount({
    pendingChangesCounts,
    data: liabilities,
  });

  return (
    <TableWrapper tableName={tableName} onToggle={onToggle}>
      {isExpanded && (
        <div>
          <AdditionalNavigationContent />
        </div>
      )}

      <TableCDS
        withAddButton
        config={config}
        customStyles={tableStyles}
        data={formattedData}
        getCellPendingChangeAuthors={getCellPendingChangeAuthors}
        isCustomDataDefinition={!liabilities.length}
        isLoading={isLoading}
        isPreview={!isExpanded}
        isWithDivider={false}
        pendingChangesCount={totalPendingChangesCount}
        title={FINANCIAL_INFO_TABLE_NAME[tableName]}
        onAddClick={onAddClick}
      />
    </TableWrapper>
  );
};

Liabilities.propTypes = {
  ...TablePropTypes,
};

export default withLoading(Liabilities);
